import {
  IAttribute,
  IAttributeFilter,
  IAttributeResponse,
  ICreateAttribute,
  IUpdateAttribute,
} from '../interfaces/IAttribute';
import { RootState } from '../redux/store';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_BASE_URL } from '../helpers/common';
import { AttributeMapper } from '../mappers/attribute.mapper';
import { AttributeEntityType } from '../enums/attribute.enum';

export const attributesApi = createApi({
  reducerPath: 'attributesApi',
  tagTypes: ['Attribute'],
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as RootState).user.info.accessToken;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (build) => ({
    getEffectiveSchema: build.query<IAttribute[], AttributeEntityType>({
      transformResponse: (response: { data: IAttribute[] }) =>
        AttributeMapper.toListUI(response.data ?? []),
      query: (entityType) => ({
        url: `/attributes/schema/${entityType}`,
        method: 'GET',
      }),
      providesTags: () => ['Attribute'],
    }),
    getAttributesList: build.query<
      IAttributeResponse,
      IAttributeFilter
    >({
      transformResponse: (response: { data: IAttributeResponse }) => {
        const data = response.data;
        return {
          ...data,
          list: AttributeMapper.toListUI(data.list || []),
        };
      },
      query: (params: IAttributeFilter) => ({
        url: '/attributes/list',
        method: 'GET',
        params: {
          ...params,
          entityType: params.entityType || undefined,
        },
      }),
      providesTags: () => ['Attribute'],
    }),
    getAttribute: build.query<IAttribute, number | string>({
      transformResponse: (response: { data: IAttribute }) => 
        AttributeMapper.toLocal(response.data),
      query: (id) => ({
        url: `/attributes/${id}`,
        method: 'GET',
      }),
      providesTags: () => ['Attribute'],
    }),
    createAttribute: build.mutation<IAttribute, ICreateAttribute>({
      transformResponse: (response: { data: IAttribute }) => 
        AttributeMapper.toLocal(response.data),
      query: (newAttribute) => ({
        url: '/attributes/create',
        method: 'POST',
        body: AttributeMapper.toCreateRequest(newAttribute),
      }),
      invalidatesTags: ['Attribute'],
    }),
    updateAttribute: build.mutation<
      IAttribute,
      { id: number | string; data: IUpdateAttribute }
    >({
      transformResponse: (response: { data: IAttribute }) => 
        AttributeMapper.toLocal(response.data),
      query: ({ id, data }) => ({
        url: `/attributes/${id}`,
        method: 'PATCH',
        body: AttributeMapper.toUpdateRequest(Number(id), data),
      }),
      invalidatesTags: ['Attribute'],
    }),
    deleteAttribute: build.mutation<IAttribute, number>({
      query: (attributeId) => ({
        url: `/attributes/${attributeId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Attribute'],
    }),
  }),
});

export default attributesApi;

